import type { PresetSortAccessors } from '@/common/helpers/apply-preset';
import { isUnknownKeywordDifficultyValue } from '@/modules/keywords/helpers/sort';
import type { KeywordIdeaData } from '@/modules/keywords/stores/keywords';
import type { KeywordFilter } from '@/modules/keywords/types';

const toSortableNumber = (value: unknown): number => {
  return (typeof value === 'number' && Number.isFinite(value)) ? value : Number.NaN;
};

export const SORT_ACCESSORS: PresetSortAccessors<KeywordIdeaData> = {
  keyword:            (r) => r.keyword.toLowerCase(),
  character_count:    (r) => r.character_count,
  tag_occurrences:    (r) => r.occurrences,
  search_trend:       (r) => toSortableNumber(r.search_trend?.at(-1)?.value),
  avg_searches:       (r) => toSortableNumber(r.avg_searches.order_value),
  avg_clicks:         (r) => toSortableNumber(r.avg_clicks?.order_value),
  ctr:                (r) => toSortableNumber(r.ctr.order_value),
  competition:        (r) => toSortableNumber(r.competition.order_value),
  keyword_difficulty: (r) => isUnknownKeywordDifficultyValue(r.keyword_difficulty?.order_value)
    ? Number.NaN
    : toSortableNumber(r.keyword_difficulty?.order_value),
  google:             (r) => toSortableNumber(r.google.order_value),
};

export const KEYWORD_TOOL_AUTOCOMPLETE_REFERRER = 'keyword-tool-autocomplete';
export const KEYWORD_TOOL_DEFAULT_REFERRER = 'keyword-tool-default';

export const COUNTRY_SPECIFIC_DISABLED_COLUMNS: Record<string, string[]> = {
  EEA: ['competition', 'keyword_difficulty'],
  GLO: ['occurrences'],
};

export const NON_USA_DISABLED_COLUMNS = ['google', 'search_trend'] as const;

export interface HoverMessages {
  keyword: string;
  search_trend: string;
  tag_occurrences: string;
  character_count: string;
  avg_searches: string;
  avg_clicks: string;
  ctr: string;
  competition: string;
  keyword_difficulty: string;
  google: string;
}

export const HOVER_MESSAGES: HoverMessages = {
  keyword: 'These are high-ranking keywords associated with the keyword you entered.',
  search_trend: 'This graph indicates how the popularity of the keywords changed over the past 15 months. From this you can get a sense of seasonality and whether a keyword is becoming more or less popular.',
  tag_occurrences: 'The number of times we found that tag in the top 100 listings we analyzed.',
  character_count: 'This is the number of characters in the keyword phrase, including spaces and punctuation.',
  avg_searches: 'Average monthly searches based on the past 12 months of data.',
  avg_clicks: 'Average monthly clicks that listings for that keyword receive. Based on the past 12 months of data.',
  ctr: 'CTR measures the click-through rate of search results pages. A click-through rate over 100% means buyers are heavily engaged with search results, often clicking on multiple listings that appear on a page.',
  competition: 'How much competition you can expect with this keyword on Etsy search results pages.',
  keyword_difficulty: 'Keyword Difficulty shows you the ratio between search volume and competition. The lower the number, the less competition there is for this keyword versus search volume, indicating a possible opportunity in the marketplace.',
  google: 'Estimated number of searches performed on Google each month. Google search data is also useful for your website and can help you understand broader user-interest levels, keywords, and products.',
};

export const COLUMN_PREFERENCES_DICTIONARY = {
  keywords: 'Keywords',
  search_trend: 'Search Trend',
  character_count: 'Character Count',
  tag_occurrences: 'Tag Occurrences',
  avg_searches: 'Avg. Searches',
  avg_clicks: 'Avg. Clicks',
  ctr: 'Avg. CTR',
  competition: 'Etsy Competition',
  keyword_difficulty: 'Keyword Difficulty',
  google: 'Google Searches',
} as const;

export const REPORT_HEADER_DICTIONARY = {
  keywords: 'Keywords',
  search_trend: 'Search Trend',
  character_count: 'Character Count',
  tag_occurrences: 'Tag Occurrences',
  avg_searches: 'Average Searches',
  avg_clicks: 'Average Clicks',
  ctr: 'CTR',
  competition: 'Competition',
  keyword_difficulty: 'Keyword Difficulty',
  google: 'Google Searches',
} as const;

export const FILTER_OPTIONS: { label: string, value: KeywordFilter }[] = [
  {
    label: 'Match: Default',
    value: 'default_match',
  },
  {
    label: 'Match: Phrase',
    value: 'phrase_match',
  },
  {
    label: 'Match: Exact',
    value: 'exact_match',
  },
  {
    label: 'Match: Broad',
    value: 'broad_match',
  },
];

export const KEYWORD_IDEAS_TABLE_COLUMNS = {
  search_trend: {
    index: 0,
    visible: true,
  },
  avg_searches: {
    index: 1,
    visible: true,
  },
  avg_clicks: {
    index: 2,
    visible: true,
  },
  ctr: {
    index: 3,
    visible: true,
  },
  competition: {
    index: 4,
    visible: true,
  },
  keyword_difficulty: {
    index: 5,
    visible: true,
  },
  tag_occurrences: {
    index: 6,
    visible: true,
  },
  character_count: {
    index: 7,
    visible: true,
  },
  google: {
    index: 8,
    visible: true,
  },
};

export const NEAR_MATCHES_TABLE_COLUMNS = {
  search_trend: {
    index: 0,
    visible: true,
  },
  avg_searches: {
    index: 1,
    visible: true,
  },
  avg_clicks: {
    index: 2,
    visible: true,
  },
  ctr: {
    index: 3,
    visible: true,
  },
  competition: {
    index: 4,
    visible: true,
  },
  keyword_difficulty: {
    index: 5,
    visible: true,
  },
  tag_occurrences: {
    index: 6,
    visible: true,
  },
  character_count: {
    index: 7,
    visible: true,
  },
  google: {
    index: 8,
    visible: true,
  },
};

export const TOP_LISTINGS_TABLE_COLUMNS = {
  age: {
    index: 0,
    visible: true,
  },
  views: {
    index: 1,
    visible: true,
  },
  daily_views: {
    index: 2,
    visible: true,
  },
  est_sales: {
    index: 3,
    visible: true,
  },
  price: {
    index: 4,
    visible: true,
  },
  est_revenue: {
    index: 5,
    visible: true,
  },
  num_favorers: {
    index: 6,
    visible: true,
  },
};

export const POPULAR_MARKETPLACES_TABLE_COLUMNS = {
  search_trend: {
    index: 0,
    visible: true,
  },
  avg_searches: {
    index: 1,
    visible: true,
  },
  avg_clicks: {
    index: 2,
    visible: true,
  },
  avg_ctr: {
    index: 3,
    visible: true,
  },
};
